import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { buildAuthHeaders } from '../utils/auth';
import styles from './GestionMyPage.module.css';
import heic2any from 'heic2any';
import imageCompression from 'browser-image-compression';
import { ProfileAnimation } from './MyPage';
import { useLanguage } from '../utils/language';
import { getPageContainerStyles } from '../utils/styleUtils';

// 다국어 텍스트
const TEXT = {
  title: { ko: '내페이지수정', en: 'Edit My Page', fr: 'Modifier ma page' },
  loading: { ko: '로딩 중...', en: 'Loading...', fr: 'Chargement...' },
  profileImage: { ko: '프로필 이미지', en: 'Profile Image', fr: 'Image de profil' },
  removeImage: { ko: '이미지 제거', en: 'Remove Image', fr: 'Supprimer image' },
  attachImage: { ko: '첨부', en: 'Attach', fr: 'Joindre' },
  bio: { ko: '소개', en: 'Bio', fr: 'Bio' },
  bioLabel: { ko: '소개:', en: 'Bio:', fr: 'Bio:' },
  bioPlaceholder: { ko: '자기소개를 입력하세요...', en: 'Enter your bio...', fr: 'Entrez votre bio...' },
  instagram: { ko: '인스타그램', en: 'Instagram', fr: 'Instagram' },
  instagramLabel: { ko: '인스타그램:', en: 'Instagram:', fr: 'Instagram:' },
  tiktok: { ko: '틱톡', en: 'TikTok', fr: 'TikTok' },
  tiktokLabel: { ko: '틱톡:', en: 'TikTok:', fr: 'TikTok:' },
  website: { ko: '웹사이트', en: 'Website', fr: 'Site web' },
  websiteLabel: { ko: '웹사이트:', en: 'Website:', fr: 'Site web:' },
  contactEmail: { ko: '연락처이메일', en: 'Contact Email', fr: 'Email de contact' },
  contactEmailLabel: { ko: '연락처 이메일:', en: 'Contact Email:', fr: 'Email de contact:' },
  open: { ko: '공개', en: 'Open', fr: 'Ouvert' },
  reservationSettings: { ko: '예약가능상태설정', en: 'Reservation Settings', fr: 'Paramètres de réservation' },
  acceptFlashReservations: { ko: '플래시예약받기', en: 'Accept Flash Reservations', fr: 'Accepter réservations flash' },
  acceptPersonalProjects: { ko: '개인프로젝트받기', en: 'Accept Personal Projects', fr: 'Accepter projets personnels' },
  depositSettings: { ko: '선금설정', en: 'Deposit Settings', fr: 'Paramètres d\'acompte' },
  useDefaultDeposit: { ko: '사용하기', en: 'Use', fr: 'Utiliser' },
  usePaypal: { ko: '사용하기', en: 'Use', fr: 'Utiliser' },
  useBank: { ko: '사용하기', en: 'Use', fr: 'Utiliser' },
  useLydia: { ko: '사용하기', en: 'Use', fr: 'Utiliser' },
  defaultDepositAmount: { ko: '기본선금금액', en: 'Default Deposit Amount', fr: 'Montant acompte par défaut' },
  paypalAddress: { ko: '페이팔주소', en: 'PayPal Address', fr: 'Adresse PayPal' },
  paypalPlaceholder: { ko: 'example@paypal.com', en: 'example@paypal.com', fr: 'example@paypal.com' },
  bankAccountInfo: { ko: '은행계좌정보', en: 'Bank Account Info', fr: 'Infos compte bancaire' },
  bankPlaceholder: { ko: '은행명/계좌번호/예금주', en: 'Bank/Account/Name', fr: 'Banque/Compte/Nom' },
  lydiaAddress: { ko: '리디아주소', en: 'Lydia Address', fr: 'Adresse Lydia' },
  lydiaPlaceholder: { ko: 'Lydia 주소', en: 'Lydia address', fr: 'Adresse Lydia' },
  depositMessageTitle: { ko: '위 설정에따라 아래와같이 선금메세지가 설정됩니다.', en: 'Based on the above settings, the deposit message will be set as follows.', fr: 'Selon les paramètres ci-dessus, le message d\'acompte sera défini comme suit.' },
  depositMessage: { ko: '선금안내메세지', en: 'Deposit Message', fr: 'Message acompte' },
  depositMessagePlaceholder: { ko: '선금 안내 메시지를 입력하세요...', en: 'Enter deposit message...', fr: 'Entrez message acompte...' },
  customDepositMessage: { ko: '페이팔/계좌/리디아 말고 다른 형식의 선금을 받는경우 아래에 커스텀메세지에 선금안내메세지를 적어주세요.', en: 'If you receive deposits in formats other than PayPal/Account/Lydia, please write the deposit message in the custom message below.', fr: 'Si vous recevez des acomptes dans des formats autres que PayPal/Compte/Lydia, veuillez écrire le message d\'acompte dans le message personnalisé ci-dessous.' },
  saving: { ko: '저장 중...', en: 'Saving...', fr: 'Enregistrement...' },
  save: { ko: '저장', en: 'Save', fr: 'Enregistrer' },
  savedSuccessfully: { ko: '저장되었습니다.', en: 'Saved successfully.', fr: 'Enregistré avec succès.' },
  failedToSave: { ko: '저장에 실패했습니다.', en: 'Failed to save.', fr: 'Échec de l\'enregistrement.' },
  networkError: { ko: '네트워크 오류', en: 'Network error', fr: 'Erreur réseau' },
  heicConversionError: { ko: 'HEIC 파일 변환에 실패했습니다. iPhone 카메라 설정에서 "가장 호환성 높은 형식"으로 변경하거나, jpg/png로 변환 후 업로드 해주세요.', en: 'HEIC file conversion failed. Please change to "Most Compatible" format in iPhone camera settings, or convert to jpg/png before uploading.', fr: 'Échec de conversion HEIC. Changez vers "Format le plus compatible" dans les paramètres caméra iPhone, ou convertissez en jpg/png avant upload.' },
  imageSizeError: { ko: '이미지 용량은 10MB 이하만 가능합니다.', en: 'Image size must be 10MB or less.', fr: 'La taille d\'image doit être 10MB ou moins.' },
  imageFormatError: { ko: 'jpg, jpeg, png 형식의 이미지만 업로드할 수 있습니다.', en: 'Only jpg, jpeg, png format images can be uploaded.', fr: 'Seules les images jpg, jpeg, png peuvent être uploadées.' },
  imageCompressionError: { ko: '이미지 압축/변환에 실패했습니다.', en: 'Image compression/conversion failed.', fr: 'Échec de compression/conversion d\'image.' },
  unableToLoadInfo: { ko: '정보를 불러올 수 없습니다.', en: 'Unable to load information.', fr: 'Impossible de charger les informations.' },
};

function getProfileImageUrl(img) {
  if (!img) return '';
  if (img.startsWith('http')) return img;
  return img;
}

function GestionMyPage() {
  const { userInfo } = useContext(UserContext);
  const artistUserId = userInfo?.user_id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // 사용자 언어 설정 사용
  const lang = useLanguage();

  // 공통 스타일 가져오기
  const pageContainerStyles = getPageContainerStyles();

  // 상태
  const [profileImage, setProfileImage] = useState(null); // File
  const [profileImageUrl, setProfileImageUrl] = useState(''); // 미리보기
  const [bio, setBio] = useState('');
  const [bioPublic, setBioPublic] = useState(true);
  const [instagram, setInstagram] = useState('');
  const [instagramPublic, setInstagramPublic] = useState(true);
  const [tiktok, setTiktok] = useState('');
  const [tiktokPublic, setTiktokPublic] = useState(true);
  const [website, setWebsite] = useState('');
  const [websitePublic, setWebsitePublic] = useState(true);
  const [contactEmail, setContactEmail] = useState('');
  const [contactEmailPublic, setContactEmailPublic] = useState(true);
  const [flashReservationOpen, setFlashReservationOpen] = useState(true);
  const [projectOpen, setProjectOpen] = useState(true);

  const [usePaypal, setUsePaypal] = useState(false);
  const [useBank, setUseBank] = useState(false);
  const [useLydia, setUseLydia] = useState(false);
  const [defaultDepositAmount, setDefaultDepositAmount] = useState('');
  const [paypalAddress, setPaypalAddress] = useState('');
  const [bankAccountInfo, setBankAccountInfo] = useState('');
  const [lydiaAddress, setLydiaAddress] = useState('');
  const [customDepositMessage, setCustomDepositMessage] = useState('');
  const [imgError, setImgError] = useState(false);

  // 아티스트 정보 불러오기
  useEffect(() => {
    if (!artistUserId) return;
    setLoading(true);
    fetch(`/api/artists/${artistUserId}`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        setProfileImageUrl(data.profile_image || '');
        setBio(data.bio || '');
        setBioPublic(data.bio_public !== false);
        setInstagram(data.instagram || '');
        setInstagramPublic(data.instagram_public !== false);
        setTiktok(data.tiktok || '');
        setTiktokPublic(data.tiktok_public !== false);
        setWebsite(data.website || '');
        setWebsitePublic(data.website_public !== false);
        setContactEmail(data.contact_email || '');
        setContactEmailPublic(data.contact_email_public !== false);
        setFlashReservationOpen(data.reservation_open !== false);
        setProjectOpen(data.project_open !== false);
        setUsePaypal(data.use_paypal || false);
        setUseBank(data.use_bank || false);
        setUseLydia(data.use_lydia || false);
        setDefaultDepositAmount(data.default_deposit_amount || '');
        setPaypalAddress(data.paypal_address || '');
        setBankAccountInfo(data.bank_account_info || '');
        setLydiaAddress(data.lydia_address || '');
        setCustomDepositMessage(data.deposit_message || '');
      })
      .catch(() => setError(TEXT.unableToLoadInfo[lang]))
      .finally(() => setLoading(false));
  }, [artistUserId, lang]);

  // 자동 높이 조정 함수
  const adjustTextareaHeight = (element) => {
    element.style.height = 'auto';
    element.style.height = element.scrollHeight + 'px';
  };

  // Bio 자동 높이 조정을 위한 useEffect
  useEffect(() => {
    const bioTextarea = document.querySelector(`.${styles.textarea}[data-field="bio"]`);
    if (bioTextarea) {
      adjustTextareaHeight(bioTextarea);
    }
  }, [bio]);

  // Custom Deposit Message 자동 높이 조정을 위한 useEffect
  useEffect(() => {
    const customDepositTextarea = document.querySelector(`.${styles.textarea}[data-field="customDeposit"]`);
    if (customDepositTextarea) {
      adjustTextareaHeight(customDepositTextarea);
    }
  }, [customDepositMessage]);

  // 이미지 업로드 핸들러
  const handleImageChange = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    // 1. heic → jpg 변환 시도
    if (file.type === 'image/heic' || file.name.endsWith('.heic')) {
      try {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.95 });
        file = new File([converted], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
      } catch (err) {
        setError(TEXT.heicConversionError[lang]);
        return;
      }
    }
    // 2. 파일 크기 체크 (10MB 이하, 변환 후 기준)
    if (file.size > 10 * 1024 * 1024) {
      setError(TEXT.imageSizeError[lang]);
      return;
    }
    // 3. 확장자 체크 (jpg, jpeg, png만 허용)
    const allowed = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      setError(TEXT.imageFormatError[lang]);
      return;
    }
    // 4. 고화질 압축(최대 2000px, 95% 품질)
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 10, maxWidthOrHeight: 2000, initialQuality: 0.95, useWebWorker: true });
      setProfileImage(compressed);
      setError('');
      const reader = new FileReader();
      reader.onload = (ev) => setProfileImageUrl(ev.target.result);
      reader.readAsDataURL(compressed);
    } catch (err) {
      setError(TEXT.imageCompressionError[lang]);
    }
  };
  const handleImageRemove = () => {
    setProfileImage(null);
    setProfileImageUrl('');
    setImgError(false);
  };

  // 저장
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const formData = new FormData();
      if (profileImage) formData.append('profile_image', profileImage);
      formData.append('bio', bio);
      formData.append('bio_public', bioPublic);
      formData.append('instagram', instagram);
      formData.append('instagram_public', instagramPublic);
      formData.append('tiktok', tiktok);
      formData.append('tiktok_public', tiktokPublic);
      formData.append('website', website);
      formData.append('website_public', websitePublic);
      formData.append('contact_email', contactEmail);
      formData.append('contact_email_public', contactEmailPublic);
      formData.append('reservation_open', flashReservationOpen);
      formData.append('project_open', projectOpen);
      formData.append('use_paypal', usePaypal);
      formData.append('use_bank', useBank);
      formData.append('use_lydia', useLydia);
      formData.append('default_deposit_amount', defaultDepositAmount);
      formData.append('paypal_address', paypalAddress);
      formData.append('bank_account_info', bankAccountInfo);
      formData.append('lydia_address', lydiaAddress);
      formData.append('deposit_message', customDepositMessage);
      const res = await fetch(`/api/artists/${userInfo.user_id}`, {
        method: 'PUT',
        headers: buildAuthHeaders(),
        credentials: 'include',
        body: formData,
      });
      if (res.ok) {
        setMessage(TEXT.savedSuccessfully[lang]);
      } else {
        // 서버에서 온 에러 메시지 표시
        let msg = TEXT.failedToSave[lang];
        try {
          const err = await res.json();
          if (err && err.message) msg = err.message;
        } catch { }
        setError(msg);
      }
    } catch (err) {
      setError(TEXT.networkError[lang]);
    } finally {
      setSaving(false);
    }
  };

  // 자동 생성되는 선금 메시지
  const generateDepositMessage = () => {
    const depositMessageTexts = {
      ko: {
        title: '[선금 안내]',
        intro: '예약 확정을 위해 선금이 필요합니다.',
        amountWithValue: `선금 금액: ${defaultDepositAmount}€`,
        amountWithoutValue: '선금 금액: 별도 안내',
        paymentMethods: '결제 방법:',
        paypal: '페이팔',
        lydia: '리디아',
        bankAccount: '은행계좌',
        bankTransferNote: '※ 은행 이체 시: 즉시이체가 아닌 경우 이체 완료 후 캡처를 보내주세요.',
        memoNote: '※ 결제 시 메모에 본인을 식별할 수 있는 정보를 입력해주세요. (아이디, 닉네임, 이름)',
        completionNote: '선금 완료 후, 결제 방법과 함께 완료 메시지를 보내주시면 예약을 확정해드리겠습니다.',
        autoCancelNote: '※ 선금 미입금 시 3일 후 예약이 자동 취소됩니다.',
        thankYou: '감사합니다.'
      },
      en: {
        title: '[Deposit Notice]',
        intro: 'A deposit is required to confirm your reservation.',
        amountWithValue: `Deposit amount: ${defaultDepositAmount}€`,
        amountWithoutValue: 'Deposit amount: To be advised',
        paymentMethods: 'Payment methods:',
        paypal: 'PayPal',
        lydia: 'Lydia',
        bankAccount: 'Bank Account',
        bankTransferNote: '※ For bank transfers: If not an instant transfer, please send a screenshot after completion.',
        memoNote: '※ When making payment, please include identifying information in the memo (ID, nickname, name)',
        completionNote: 'After completing the deposit, please send a completion message with the payment method used, and we will confirm your reservation.',
        autoCancelNote: '※ Reservations will be automatically cancelled if no deposit is received within 3 days.',
        thankYou: 'Thank you.'
      },
      fr: {
        title: '[Avis d\'acompte]',
        intro: 'Un acompte est requis pour confirmer votre réservation.',
        amountWithValue: `Montant de l'acompte: ${defaultDepositAmount}€`,
        amountWithoutValue: 'Montant de l\'acompte: À préciser',
        paymentMethods: 'Méthodes de paiement:',
        paypal: 'PayPal',
        lydia: 'Lydia',
        bankAccount: 'Compte bancaire',
        bankTransferNote: '※ Pour les virements bancaires: Si ce n\'est pas un virement instantané, veuillez envoyer une capture d\'écran après completion.',
        memoNote: '※ Lors du paiement, veuillez inclure des informations d\'identification dans le mémo (ID, pseudo, nom)',
        completionNote: 'Après avoir complété l\'acompte, veuillez envoyer un message de completion avec la méthode de paiement utilisée, et nous confirmerons votre réservation.',
        autoCancelNote: '※ Les réservations seront automatiquement annulées si aucun acompte n\'est reçu dans les 3 jours.',
        thankYou: 'Merci.'
      }
    };

    const texts = depositMessageTexts[lang];
    let message = `${texts.title}\n\n${texts.intro}\n`;

    if (defaultDepositAmount) {
      message += `${texts.amountWithValue}\n\n`;
    } else {
      message += `${texts.amountWithoutValue}\n\n`;
    }

    const platforms = [];
    if (usePaypal && paypalAddress) platforms.push(`• ${texts.paypal}: ${paypalAddress}`);
    if (useLydia && lydiaAddress) platforms.push(`• ${texts.lydia}: ${lydiaAddress}`);
    if (useBank && bankAccountInfo) platforms.push(`• ${texts.bankAccount}: ${bankAccountInfo}`);

    if (platforms.length > 0) {
      message += `${texts.paymentMethods}\n`;
      message += platforms.join('\n') + '\n\n';

      if (useBank && bankAccountInfo) {
        message += `${texts.bankTransferNote}\n\n`;
      }

      message += `${texts.memoNote}\n\n`;
    }

    message += `${texts.completionNote}\n\n${texts.autoCancelNote}\n\n${texts.thankYou}`;
    return message;
  };



  // 디버깅 로그
  console.log('[DEBUG] profileImageUrl:', profileImageUrl, '| imgError:', imgError);

  return (
    <div className={pageContainerStyles.pageContainer}>
      <h1 className={pageContainerStyles.pageTitle}>{TEXT.title[lang]}</h1>
      {loading ? <div className={styles.loading}>{TEXT.loading[lang]}</div> : (
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* 컨테이너1: 프로필 이미지 */}
          <div style={{
            border: '1px solid #000',
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              {(profileImageUrl && !imgError) ? (
                <img
                  src={getProfileImageUrl(profileImageUrl)}
                  alt="profile"
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #eee'
                  }}
                  onError={() => setImgError(true)}
                />
              ) : (
                <ProfileAnimation />
              )}
              <button type="button" onClick={handleImageRemove} style={{
                padding: '6px 12px',
                background: '#fff',
                color: '#111',
                border: '1px solid #111',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}>
                {TEXT.removeImage[lang]}
              </button>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                padding: '6px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* 컨테이너2: 소개 및 연락처 정보 */}
          <div style={{
            border: '1px solid #000',
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {/* 소개 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.bioLabel[lang]}
                </span>
                <input
                  value={bio}
                  onChange={e => {
                    setBio(e.target.value);
                    adjustTextareaHeight(e.target);
                  }}
                  placeholder={TEXT.bioPlaceholder[lang]}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                  <input type="checkbox" checked={bioPublic} onChange={e => setBioPublic(e.target.checked)} />
                  {TEXT.open[lang]}
                </label>
              </div>
            </div>

            {/* 인스타그램 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.instagramLabel[lang]}
                </span>
                <input
                  value={instagram}
                  onChange={e => setInstagram(e.target.value)}
                  placeholder="Instagram"
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                  <input type="checkbox" checked={instagramPublic} onChange={e => setInstagramPublic(e.target.checked)} />
                  {TEXT.open[lang]}
                </label>
              </div>
            </div>

            {/* 틱톡 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.tiktokLabel[lang]}
                </span>
                <input
                  value={tiktok}
                  onChange={e => setTiktok(e.target.value)}
                  placeholder="TikTok"
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                  <input type="checkbox" checked={tiktokPublic} onChange={e => setTiktokPublic(e.target.checked)} />
                  {TEXT.open[lang]}
                </label>
              </div>
            </div>

            {/* 웹사이트 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.websiteLabel[lang]}
                </span>
                <input
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                  placeholder="Website"
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                  <input type="checkbox" checked={websitePublic} onChange={e => setWebsitePublic(e.target.checked)} />
                  {TEXT.open[lang]}
                </label>
              </div>
            </div>

            {/* 연락처이메일 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.contactEmailLabel[lang]}
                </span>
                <input
                  value={contactEmail}
                  onChange={e => setContactEmail(e.target.value)}
                  placeholder="Contact Email"
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                  <input type="checkbox" checked={contactEmailPublic} onChange={e => setContactEmailPublic(e.target.checked)} />
                  {TEXT.open[lang]}
                </label>
              </div>
            </div>
          </div>

          {/* 컨테이너3: 예약 가능 상태 설정 */}
          <div style={{
            border: '1px solid #000',
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '500' }}>
              {TEXT.reservationSettings[lang]}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                <input type="checkbox" checked={flashReservationOpen} onChange={e => setFlashReservationOpen(e.target.checked)} />
                {TEXT.acceptFlashReservations[lang]}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                <input type="checkbox" checked={projectOpen} onChange={e => setProjectOpen(e.target.checked)} />
                {TEXT.acceptPersonalProjects[lang]}
              </label>
            </div>
          </div>

          {/* 컨테이너4: 선금 설정 */}
          <div style={{
            border: '1px solid #000',
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '500' }}>
              {TEXT.depositSettings[lang]}
            </h3>

            {/* 기본 선금 금액 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.defaultDepositAmount[lang]}:
                </span>
                <input
                  type="number"
                  value={defaultDepositAmount}
                  onChange={e => setDefaultDepositAmount(e.target.value)}
                  placeholder="0"
                  min="0"
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* 페이팔 주소 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.paypalAddress[lang]}:
                </span>
                <input
                  value={paypalAddress}
                  onChange={e => setPaypalAddress(e.target.value)}
                  placeholder={TEXT.paypalPlaceholder[lang]}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                  <input type="checkbox" checked={usePaypal} onChange={e => setUsePaypal(e.target.checked)} />
                  {TEXT.usePaypal[lang]}
                </label>
              </div>
            </div>

            {/* 은행 계좌 정보 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.bankAccountInfo[lang]}:
                </span>
                <input
                  value={bankAccountInfo}
                  onChange={e => setBankAccountInfo(e.target.value)}
                  placeholder={TEXT.bankPlaceholder[lang]}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                  <input type="checkbox" checked={useBank} onChange={e => setUseBank(e.target.checked)} />
                  {TEXT.useBank[lang]}
                </label>
              </div>
            </div>

            {/* 리디아 주소 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  minWidth: '120px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {TEXT.lydiaAddress[lang]}:
                </span>
                <input
                  value={lydiaAddress}
                  onChange={e => setLydiaAddress(e.target.value)}
                  placeholder={TEXT.lydiaPlaceholder[lang]}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                  <input type="checkbox" checked={useLydia} onChange={e => setUseLydia(e.target.checked)} />
                  {TEXT.useLydia[lang]}
                </label>
              </div>
            </div>
          </div>

          {/* 자동 생성 선금 메시지 */}
          <div style={{
            border: '1px solid #000',
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '500' }}>
              {TEXT.depositMessageTitle[lang]}
            </h3>
            <div style={{
              padding: '16px',
              background: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              whiteSpace: 'pre-line',
              lineHeight: '1.5',
              fontSize: '14px',
              fontFamily: 'monospace'
            }}>
              {generateDepositMessage()}
            </div>
          </div>

          {/* 커스텀 선금 메시지 */}
          <div style={{
            border: '1px solid #000',
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>
              {TEXT.customDepositMessage[lang]}
            </p>
            <textarea
              value={customDepositMessage}
              onChange={e => {
                setCustomDepositMessage(e.target.value);
                adjustTextareaHeight(e.target);
              }}
              className={styles.textarea}
              data-field="customDeposit"
              placeholder={TEXT.depositMessagePlaceholder[lang]}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <button type="submit" disabled={saving} style={{
            padding: '12px 24px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            {saving ? TEXT.saving[lang] : TEXT.save[lang]}
          </button>
          {error && <div style={{ marginTop: '12px', padding: '12px', background: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>{error}</div>}
          {message && <div style={{ marginTop: '12px', padding: '12px', background: '#d4edda', color: '#155724', borderRadius: '4px' }}>{message}</div>}
        </form>
      )}
    </div>
  );
}

export default GestionMyPage; 
